import SvgQuote from '@img/icons/quote-rounded.svg?url';
import { css } from 'styled-components';
import { transition } from './animate';
import brand from './brand';
import { fluid } from './fluid';
import fonts from './fonts';
import { Device, from } from './media';
export const headingStyles = css `
  ${fonts.lustria.regular};
  color: currentColor;
  margin: 0 0 ${fluid(24, 32)};
`;
export const h1 = css `
  ${headingStyles};

  font-size: ${fluid(43, 72)};
  line-height: ${fluid(55, 93)};
`;
export const h2 = css `
  ${headingStyles};

  font-size: ${fluid(40, 62)};
  line-height: ${fluid(50, 90)};
`;
export const h3 = css `
  ${headingStyles};

  font-size: ${fluid(35, 56)};
  line-height: ${fluid(45, 66)};
`;
export const h4 = css `
  ${headingStyles};

  font-size: ${fluid(30, 50)};
  line-height: ${fluid(40, 60)};
`;
export const h5 = css `
  ${headingStyles};

  font-size: ${fluid(22, 36)};
  line-height: ${fluid(36, 50)};
`;
export const h6 = css `
  ${headingStyles};

  font-size: ${fluid(20, 38)};
  line-height: ${fluid(30, 48)};
`;
export const h7 = css `
  ${headingStyles};
  ${fonts.montserrat.medium}

  font-size: ${fluid(18, 31)};
  line-height: ${fluid(28, 41)};
`;
export const h8 = css `
  ${headingStyles};
  ${fonts.montserrat.medium}

  font-size: ${fluid(16, 26)};
  line-height: ${fluid(26, 36)};
`;
export const paragraphStyles = css `
  ${fonts.montserrat.regular};
  color: currentColor;
  margin: 0 0 ${fluid(16, 22)};
`;
export const paragraphLarge = css `
  ${paragraphStyles};

  font-size: ${fluid(18, 24)};
  line-height: ${fluid(28, 34)};
`;
export const paragraph = css `
  ${paragraphStyles};

  font-size: ${fluid(16, 19)};
  line-height: ${fluid(27, 29)};
`;
export const paragraphSmall = css `
  ${paragraphStyles};

  font-size: ${fluid(16, 18)};
  line-height: ${fluid(26, 28)};
`;
export const paragraphExtraSmall = css `
  ${paragraphStyles};

  font-size: ${fluid(12, 14)};
  line-height: ${fluid(23, 25)};
`;
export const caption = css `
  ${paragraphSmall};
  ${fonts.montserrat.medium};
  text-transform: uppercase;
  opacity: 0.5;
`;
export const label = css `
  ${paragraphStyles};
  ${fonts.montserrat.medium};

  font-size: ${fluid(16, 18)};
  line-height: ${fluid(26, 34)};
  text-transform: uppercase;
`;
export const link = css `
  ${fonts.montserrat.regular};
  ${transition('text-decoration-color')};

  display: inline-block;
  text-decoration: underline;
  text-decoration-color: currentColor;
  text-decoration-thickness: 1px;
  text-underline-offset: 2px;

  &:focus {
    outline: none;
  }

  &:focus,
  &:hover {
    text-decoration-color: transparent;
  }
`;
export const subtitle = css `
  ${paragraphStyles};

  font-size: ${fluid(16, 18)};
  line-height: ${fluid(25, 28)};
`;
export const blockquote = css `
  ${paragraphStyles};
  font-weight: 200;
  color: currentColor;
  font-size: ${fluid(16, 19)};
  line-height: ${fluid(25, 29)};
  margin: 0 0 ${fluid(14, 27)};
  padding-left: 28px;
  text-align: left;
  position: relative;

  &::before {
    content: '\\201C';
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    font-size: 60px;
    line-height: 60px;
  }
`;
export const blockquoteName = css `
  ${paragraphStyles};
  font-weight: 400;
  font-size: ${fluid(14, 16)};
  line-height: ${fluid(24, 26)};
  padding-left: 28px;
  text-align: left;

  > strong {
    display: block;
  }
`;
export const table = css `
  border-collapse: collapse;
  margin-bottom: 1em;
  width: 100%;

  thead {
    tr {
      td,
      th {
        font-weight: 700;
        font-size: 18px;
        line-height: 22px;
        padding: 12px 16px;
        text-align: left;
      }
    }

    tr[style*='text-align' i] {
      td,
      th {
        text-align: inherit;
      }
    }
  }

  tbody {
    tr {
      th {
        font-weight: 700;
        font-size: 18px;
        line-height: 22px;
        padding: 12px 16px;
        text-align: left;
      }

      td {
        font-weight: 400;
        font-size: 16px;
        line-height: 20px;
        padding: 12px 16px;
        text-align: left;
      }
    }

    tr[style*='text-align' i] {
      td,
      th {
        text-align: inherit;
      }
    }
  }
`;
export const contentStyles = css `
  h1,
  .h1-styles {
    ${h1}
  }

  h2,
  .h2-styles {
    ${h2}
  }

  h3,
  .h3-styles {
    ${h3}
  }

  h4,
  .h4-styles {
    ${h4}
  }

  h5,
  .h5-styles {
    ${h5}
  }

  .subtitle {
    ${subtitle}
  }

  p {
    ${paragraph}

    a {
      ${link};
      color: ${brand.green.link};
    }
  }

  .large {
    ${paragraphLarge}
  }

  .small {
    ${paragraphSmall}
  }

  a {
    ${link}
    color: ${brand.green.base};
  }

  .button-outline {
    position: relative;
    background-color: transparent;
    color: ${brand.black};
    font-family: ${fonts.montserrat.bold};
    border: none;
    text-decoration: none;
    --borderThickness: 2px;
    --borderPosition: -2px;
    font-size: ${fluid(18, 22)};

    &:after {
      content: '';
      position: absolute;
      bottom: var(--borderPosition);
      left: 0;

      height: var(--borderThickness);
      width: 100%;
      background-color: var(--primaryPageThemeColor);
      transition: background-color 0.3s;
    }

    &:hover {
      &:after {
        background-color: var(--primaryPageThemeColorLight);
      }
    }

    a {
      color: inherit;
      text-decoration: none;
    }
  }

  ol,
  ul {
    margin: ${fluid(8, 12)} 0 ${fluid(24, 32)};

    li {
      ${paragraphStyles};
      font-size: ${fluid(16, 18)};
      line-height: ${fluid(24, 27)};
      padding: 8px 0;
      margin: 0;

      a {
        ${link};
        color: ${brand.green.link};
      }

      p {
        display: inline;
        &:last-child {
          margin-bottom: 0;
        }

        
      }
    }
  }

  ol {
    list-style: decimal;
    padding-left: 1.2em;
  }

  ul {
    list-style: disc;
    padding-left: 1.5em;
  }

  blockquote {
    ${blockquote};

    &.expert-quote {
      background-color: var(--primaryPageThemeColorTint);
      border-radius: ${fluid(8, 16)};
      padding: ${fluid(16, 32)};
      overflow: hidden;

      h2,
      h3,
      h4,
      h5,
      h6 {
        font-size: ${fluid(24, 32)};
        line-height: ${fluid(32, 40)};
        margin-bottom: ${fluid(24, 34)};

        &:first-child {
          display: flex;
          gap: ${fluid(12, 32)};
          flex-direction: column;

          @media ${from(Device.Tablet)} {
            flex-direction: row;
            align-items: center;

            &::before {
              flex: 0 0 ${fluid(72, 92)};
            }
          }
        }
      }

      > * {
        position: relative;

        &:first-child:not(h2, h3, h4, h5, h6) {
          &::before {
            float: left;
            margin: 0 ${fluid(12, 32)} ${fluid(12, 24)} 0;
          }
        }

        &:last-child {
          margin-bottom: 0;
        }
      }

      > *:first-child {
        &::before {
          width: ${fluid(72, 92)};
        }
      }

      footer {
        display: flex;
        gap: ${fluid(16, 22)};
        margin-top: ${fluid(16, 34)};

        img {
          float: none;
          margin: 0;
          width: ${fluid(50, 72)};
          height: ${fluid(50, 72)};
          border: 2px solid var(--primaryPageThemeColor);
          border-radius: 5000px;
        }

        p {
          ${fonts.lustria.regular};

          margin: 0;
          display: flex;
          flex-direction: column;
          gap: 2px;
          font-size: ${fluid(16, 24)};
          line-height: ${fluid(24, 34)};

          cite {
            ${fonts.montserrat.regular};

            font-style: normal;
            font-size: ${fluid(14, 19)};
            line-height: ${fluid(20, 28)};
          }
        }

        @media ${from(Device.Tablet)} {
          align-items: center;
        }
      }

      & > *:first-child::before,
      &::before {
        display: block;
        content: '';
        -webkit-mask-image: url(${SvgQuote});
        mask-image: url(${SvgQuote});
        mask-repeat: no-repeat;
        mask-size: contain;
        aspect-ratio: 1.15;
        background: var(--primaryPageThemeColor);
      }

      &::before {
        position: absolute;
        top: 10%;
        left: 50%;
        transform: translateX(-50%);
        width: 110%;
        opacity: 0.1;

        @media ${from(Device.Tablet)} {
          top: auto;
          right: 0;
          bottom: 14px;
          left: auto;
          transform: translateX(5%);
          width: 362px;
        }
      }
    }
  }

  .quote-name {
    ${blockquoteName}
  }

  hr {
    display: block;
    background: currentColor;
    border: 0;
    height: 2px;
    margin: ${fluid(32, 48)} 0;
    padding: 0;
    width: 100%;
  }

  .text-align-center {
    text-align: center;
  }

  .text-align-right {
    text-align: right;
  }

  table {
    ${table};
  }

  .responsive-table {
    position: relative;
    overflow-x: auto;
  }

  > *:first-child {
    margin-top: 0;
  }

  > *:last-child {
    margin-bottom: 0;
  }
`;
